import { apiClient } from './client';

/**
 * Where each session's data is on its way in (SESSION-PIPELINE): sensor data,
 * video, video prep, scoring, transcript. One call answers a whole session
 * list, so the tile dots and the strip on the trace card share it.
 */

export type StepState =
  | 'done'
  | 'active'
  | 'waiting'
  | 'warning'
  | 'failed'
  | 'skipped';

export type StepKey = 'sensor' | 'video' | 'video_prep' | 'scoring' | 'transcript';

export interface PipelineStep {
  key: StepKey;
  label: string;
  state: StepState;
  detail?: string | null;
  done?: number | null;
  total?: number | null;
  /** 0–100 for an upload a device is reporting bytes on. */
  percent?: number | null;
}

export interface SessionPipeline {
  session_id: string;
  steps: PipelineStep[];
  overall: 'done' | 'in_progress' | 'warning' | 'failed';
}

/** Repeated `session_id=` keys — the API reads a list, not `session_id[]`. */
function idParams(sessionIds: string[], extra: Record<string, string> = {}) {
  const p = new URLSearchParams(extra);
  for (const id of sessionIds) p.append('session_id', id);
  return p;
}

/** Tenant admin: any session in the tenant. */
export async function getTenantSessionPipelines(
  tenantId: string,
  sessionIds: string[],
): Promise<SessionPipeline[]> {
  if (sessionIds.length === 0) return [];
  const { data } = await apiClient.get<SessionPipeline[]>(
    `/api/v1/tenants/${tenantId}/session-pipeline`,
    { params: idParams(sessionIds) },
  );
  return data;
}

/** Participant: their own sessions in one tenant. */
export async function getMySessionPipelines(
  tenantId: string,
  sessionIds: string[],
): Promise<SessionPipeline[]> {
  if (sessionIds.length === 0) return [];
  const { data } = await apiClient.get<SessionPipeline[]>(
    '/api/v1/me/session-pipeline',
    { params: idParams(sessionIds, { tenant_id: tenantId }) },
  );
  return data;
}
